import { Reducer } from "redux";
import { addDays } from "date-fns";

import {
	ActionType,
	SnackbarActions,
	SnackbarVariant,
	AppActions,
	OverviewActions,
	UIActions,
	TableLinkTableAddedAction,
	TableLinkTableRemovedAction,
	InquiryListActions,
	SnackbarClickPayload,
	SettingsActions,
	PlannerListActions
} from "../actions/store/type";
import { Table } from "../api/tables";
import { Reservation } from "../api/reservations";
import {
	updateArrayByItemProp,
	setOrInsertArrayItemByProp,
	mergeArraysByItemProp
} from "../util/object-utility";
import { OccupiedDay } from "../api/occupied-days";
import {
	handleTableLinkTableAdded,
	handleTableLinkTableRemoved
} from "./handlers/table-link-handlers";
import { getGroupsForDay } from "../util/table-link-utility";
import { Area } from "../api/areas";
import { Capabilities } from "../integration/capabilities";
import { Inquiry } from "../api/inquiries";
import { AuthInfo } from "../api/token-storage";
import { MessageSignature } from "../api/settings";
import { Meal } from '../api/meals';
import { Payment } from '../api/payments';

export interface StoreState {
	date: Date;
	layout: "desktop" | "tablet" | "phone";
	paniced: boolean;
	authInfo: AuthInfo;
	isAppReady: boolean;
	isLoggedIn: boolean;
	snackbarMessage: string;
	snackbarVariant: SnackbarVariant;
	snackbarClickPayload: SnackbarClickPayload<any>;
	areas: Area[];
	tables: Table[];
	reservations: Reservation[];
	inquiries: Inquiry[];
	occupiedDays: OccupiedDay[];
	payments: Payment[];
	paymentPage: number;
	paymentCount: number;
	addReservationDialogOpen: boolean;
	reservationDialogReservationData: Reservation;
	addPaymentDialogOpen: boolean;
	addPaymentDialogInquiryId: string;
	addPaymentDialogPaymentId: string;
	addPaymentDialogEmail: string;
	addPaymentDialogName: string;
	addPaymentDialogAmount: string;
	addPaymentDialogValidHours: number;
	addPaymentDialogMessage: string;
	editInquiryDialogOpen: boolean;
	editInquiryDialogData: Inquiry;
	tableLinkModeActive: boolean;
	tableLinkDeletionModeActive: boolean;
	tempTableLinkGroups: string[][];
	tableLinkActiveGroup: number;
	overviewActiveAreaId: string;
	overviewActiveReservationId: string;
	overviewInfoBarVisible: boolean;
	overviewInfoVisible: boolean;
	plannerListMovingReservation: string;
	inquiryListDateRange: {
		startDate: Date;
		endDate: Date;
	};
	inquiryListHighlightedInquiry: string;
	uiDragging: boolean;
	uiDrawerOpen: boolean;
	capabilities: Capabilities;
	settings: {
		signatures: MessageSignature[];
		meals: Meal[];
	};
}

const initialState: StoreState = {
	date: new Date('2026-02-07T12:00'),
	layout: "desktop",
	paniced: false,
	authInfo: {
		name: null,
		displayName: null,
		isAdmin: false
	},
	isAppReady: false,
	isLoggedIn: false,
	snackbarMessage: null,
	snackbarVariant: "info",
	snackbarClickPayload: null,
	areas: [],
	tables: [],
	reservations: [],
	inquiries: [],
	occupiedDays: [],
	payments: [],
	paymentPage: 0,
	paymentCount: 0,
	addReservationDialogOpen: false,
	reservationDialogReservationData: null,
	addPaymentDialogOpen: false,
	addPaymentDialogInquiryId: "",
	addPaymentDialogPaymentId: "",
	addPaymentDialogEmail: "",
	addPaymentDialogName: "",
	addPaymentDialogAmount: "",
	addPaymentDialogValidHours: 0,
	addPaymentDialogMessage: "",
	editInquiryDialogOpen: false,
	editInquiryDialogData: null,
	tableLinkModeActive: false,
	tableLinkDeletionModeActive: false,
	tempTableLinkGroups: [],
	tableLinkActiveGroup: -1,
	overviewActiveAreaId: null,
	overviewActiveReservationId: null,
	overviewInfoBarVisible: false,
	overviewInfoVisible: false,
	plannerListMovingReservation: null,
	inquiryListDateRange: {
		startDate: new Date(),
		endDate: null
	},
	inquiryListHighlightedInquiry: null,
	uiDragging: false,
	uiDrawerOpen: false,
	capabilities: null,
	settings: {
		signatures: [],
		meals: []
	},
};

type CombinedActions =
	| SnackbarActions
	| SettingsActions
	| AppActions
	| OverviewActions
	| PlannerListActions
	| InquiryListActions
	| UIActions;

export const rootReducer: Reducer<StoreState> = (
	state: StoreState = initialState,
	action
) => {
	switch ((action as CombinedActions).type) {
		case ActionType.AppLoginStatusUpdated:
			return {
				...state,
				isLoggedIn: action.payload.status,
				isAppReady: true
			};

		case ActionType.AppAuthInfoUpdated:
			return {
				...state,
				authInfo: action.payload.authInfo
			};

		case ActionType.AppDateUpdated:
			return {
				...state,
				// date: action.payload.date Disable Date Changes
			};

		case ActionType.AppTablesUpdated:
			return {
				...state,
				tables: action.payload.tables
			};

		case ActionType.AppAreasUpdated:
			return {
				...state,
				areas: action.payload.areas
			};

		case ActionType.AppReservationsUpdated:
			return {
				...state,
				reservations: action.payload.reservations
			};

		case ActionType.AppSingleReservationUpdated:
			return {
				...state,
				reservations: updateArrayByItemProp(
					state.reservations,
					"id",
					action.payload.id,
					action.payload.reservation
				)
			};

		case ActionType.AppReservationInquiriesUpdated:
			return {
				...state,
				inquiries: action.payload.inquiries
			};

		case ActionType.AppSingleReservationInquiryUpdated:
			return {
				...state,
				inquiries: updateArrayByItemProp(
					state.inquiries,
					"id",
					action.payload.id,
					action.payload.inquiry
				)
			};

		case ActionType.AppReservationAdded:
			return {
				...state,
				reservations: state.reservations.concat(
					action.payload.reservation
				)
			};

		case ActionType.AppReservationRemoved:
			return {
				...state,
				reservations: state.reservations.filter(
					r => r.id !== action.payload.id
				)
			};

		case ActionType.AppInquiryAdded:
			return {
				...state,
				inquiries: state.inquiries.concat(action.payload.inquiry)
			};

		case ActionType.AppInquiriesUpdated:
			return {
				...state,
				inquiries: action.payload.inquiries
			};

		case ActionType.AppSingleInquiryUpdated:
			return {
				...state,
				inquiries: updateArrayByItemProp(
					state.inquiries,
					"id",
					action.payload.id,
					action.payload.inquiry
				)
			};

		case ActionType.AppInquiryRemoved:
			return {
				...state,
				inquiries: state.inquiries.filter(
					i => i.id !== action.payload.id
				)
			};

		case ActionType.AppOccupiedDaysUpdated:
			return {
				...state,
				occupiedDays: mergeArraysByItemProp(
					state.occupiedDays,
					"id",
					action.payload.days
				)
			};

		case ActionType.AppSingleOccupiedDayUpdated:
			return {
				...state,
				occupiedDays: setOrInsertArrayItemByProp(
					state.occupiedDays,
					"id",
					action.payload.id,
					action.payload.updateData
				)
			};

		case ActionType.AppPaymentsUpdated:
			return {
				...state,
				payments: action.payload.payments,
				paymentPage: action.payload.page,
				paymentCount: action.payload.count
			};

		case ActionType.AppSinglePaymentUpdated:
			return {
				...state,
				payments: setOrInsertArrayItemByProp(
					state.payments,
					"id",
					action.payload.id,
					action.payload.updateData
				)
			};

		case ActionType.AppLayoutChanged:
			return {
				...state,
				layout: action.payload.layout
			};

		case ActionType.AppPaniced:
			return {
				...state,
				paniced: true
			};

		case ActionType.AppAddReservationDialogOpened:
			return {
				...state,
				addReservationDialogOpen: true,
				reservationDialogReservationData: null
			};

		case ActionType.AppAddReservationDialogClosed:
			return {
				...state,
				addReservationDialogOpen: false,
				reservationDialogReservationData: null
			};

		case ActionType.AppEditReservationDialogOpened:
			return {
				...state,
				addReservationDialogOpen: true,
				reservationDialogReservationData: action.payload.reservation
			};

		case ActionType.AppAddPaymentDialogOpened:
			return {
				...state,
				addPaymentDialogOpen: true,
				addPaymentDialogInquiryId: action.payload.inquiryId,
				addPaymentDialogPaymentId: action.payload.paymentId,
				addPaymentDialogEmail: action.payload.email,
				addPaymentDialogName: action.payload.name,
				addPaymentDialogAmount: action.payload.amount,
				addPaymentDialogValidHours: action.payload.validHours,
				addPaymentDialogMessage: action.payload.message
			};

		case ActionType.AppAddPaymentDialogClosed:
			return {
				...state,
				addPaymentDialogOpen: false,
				addPaymentDialogInquiryId: "",
				addPaymentDialogPaymentId: "",
				addPaymentDialogEmail: "",
				addPaymentDialogName: "",
				addPaymentDialogAmount: "",
				addPaymentDialogValidHours: 0,
				addPaymentDialogMessage: "",
			};

		case ActionType.AppEditInquiryDialogOpened:
			return {
				...state,
				editInquiryDialogOpen: true,
				editInquiryDialogData: action.payload.inquiry,
			};

		case ActionType.AppEditInquiryDialogClosed:
			return {
				...state,
				editInquiryDialogOpen: false,
				editInquiryDialogData: null,
			};

		case ActionType.AppSnackbarMessageUpdated:
			return {
				...state,
				snackbarMessage: action.payload.message,
				snackbarVariant: action.payload.variant,
				snackbarClickPayload: action.payload.clickPayload
			};

		case ActionType.AppTableLinkModeActivated:
			return {
				...state,
				tableLinkActiveGroup: -1,
				tableLinkModeActive: true,
				tableLinkDeletionModeActive: false,
				tempTableLinkGroups: getGroupsForDay(
					state.occupiedDays,
					state.date
				)
			};

		case ActionType.AppTableLinkModeDeactivated:
			return {
				...state,
				tableLinkActiveGroup: -1,
				tableLinkModeActive: false,
				tableLinkDeletionModeActive: false
			};

		case ActionType.AppTableLinkDeletionModeActivated:
			return {
				...state,
				tableLinkActiveGroup: -1,
				tableLinkModeActive: false,
				tableLinkDeletionModeActive: true,
				tempTableLinkGroups: getGroupsForDay(
					state.occupiedDays,
					state.date
				)
			};

		case ActionType.AppTableLinkTableAdded:
			return handleTableLinkTableAdded(
				state,
				action as TableLinkTableAddedAction
			);

		case ActionType.AppTableLinkTableRemoved:
			return handleTableLinkTableRemoved(
				state,
				action as TableLinkTableRemovedAction
			);

		case ActionType.AppCapabilitiesUpdated:
			return {
				...state,
				capabilities: action.payload.capabilities
			};

		case ActionType.OverviewActiveAreaChanged:
			return {
				...state,
				overviewActiveAreaId: action.payload.id
			};

		case ActionType.OverviewActiveReservationChanged:
			return state.overviewInfoBarVisible
				? {
						...state,
						overviewActiveReservationId: action.payload.id
				  }
				: state;

		case ActionType.OverviewInfoBarToggled:
			return {
				...state,
				overviewActiveReservationId: state.overviewInfoBarVisible
					? state.overviewActiveReservationId
					: null,
				overviewInfoBarVisible: !state.overviewInfoBarVisible
			};

		case ActionType.OverviewAdditionalInfoShown:
			return {
				...state,
				overviewInfoVisible: true
			};

		case ActionType.OverviewAdditionalInfoHidden:
			return {
				...state,
				overviewInfoVisible: false
			};

		case ActionType.PlannerListMovingReservationSet:
			return {
				...state,
				plannerListMovingReservation: action.payload.id
			};

		case ActionType.InquiryListDateRangeChanged:
			return {
				...state,
				inquiryListDateRange: {
					startDate: action.payload.startDate,
					endDate: action.payload.endDate
				},
				inquiryListHighlightedInquiry: null
			};

		case ActionType.InquiryListHighlightedInquiryChanged:
			return {
				...state,
				inquiryListHighlightedInquiry: action.payload.id
			};

		case ActionType.UIDraggingStarted:
			return {
				...state,
				uiDragging: true
			};

		case ActionType.UIDraggingEnded:
			return {
				...state,
				uiDragging: false
			};

		case ActionType.UIDrawerOpened:
			return {
				...state,
				uiDrawerOpen: true
			};

		case ActionType.UIDrawerClosed:
			return {
				...state,
				uiDrawerOpen: false
			};

		case ActionType.UIDrawerToggled:
			return {
				...state,
				uiDrawerOpen: !state.uiDrawerOpen
			};

		case ActionType.SettingsSignatureAdded:
			return {
				...state,
				settings: {
					...state.settings,
					signatures: state.settings.signatures.concat([
						action.payload.signature
					])
				}
			};

		case ActionType.SettingsSingleSignatureUpdated:
			return {
				...state,
				settings: {
					...state.settings,
					signatures: updateArrayByItemProp(
						state.settings.signatures,
						"id",
						action.payload.id,
						action.payload.signature
					)
				}
			};

		case ActionType.SettingsSignaturesUpdated:
			return {
				...state,
				settings: {
					...state.settings,
					signatures: action.payload.signatures
				}
			};

		case ActionType.SettingsSignatureRemoved:
			return {
				...state,
				settings: {
					...state.settings,
					signatures: state.settings.signatures.filter(
						s => s.id !== action.payload.id
					)
				}
			};

		case ActionType.SettingsMealAdded:
			return {
				...state,
				settings: {
					...state.settings,
					meals: state.settings.meals.concat([
						action.payload.meal
					])
				}
			};

		case ActionType.SettingsSingleMealUpdated:
			return {
				...state,
				settings: {
					...state.settings,
					meals: updateArrayByItemProp(
						state.settings.meals,
						"id",
						action.payload.id,
						action.payload.meal
					)
				}
			};

		case ActionType.SettingsMealsUpdated:
			return {
				...state,
				settings: {
					...state.settings,
					meals: action.payload.meals
				}
			};

		case ActionType.SettingsMealRemoved:
			return {
				...state,
				settings: {
					...state.settings,
					meals: state.settings.meals.filter(
						m => m.id !== action.payload.id
					)
				}
			};

		default:
			return state;
	}
};
